import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getAsset, getAssetScanDetails } from '../../services/assetService';
import './AssetDossierScroll.css';

/**
 * Asset Dossier - Variation B: Single Page Scroll
 *
 * Design Philosophy:
 * - All information on one scrolling page
 * - Sticky navigation on side
 * - Sections collapse/expand
 * - Modern infinite scroll feel
 */

const AssetDossierScroll = () => {
  const { assetId } = useParams();
  const navigate = useNavigate();
  const [asset, setAsset] = useState(null);
  const [enrichment, setEnrichment] = useState(null);
  const [scanDetails, setScanDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState('summary');
  const [collapsedSections, setCollapsedSections] = useState({});

  // Section refs for scroll tracking
  const sectionRefs = {
    summary: useRef(null),
    attackSurface: useRef(null),
    vulnerabilities: useRef(null),
    testing: useRef(null),
    defense: useRef(null)
  };

  const sections = [
    { id: 'summary', label: 'Security Summary', icon: 'shield' },
    { id: 'attackSurface', label: 'Attack Surface', icon: 'target' },
    { id: 'vulnerabilities', label: 'Vulnerabilities', icon: 'warning' },
    { id: 'testing', label: 'Testing Approach', icon: 'flask' },
    { id: 'defense', label: 'Defense Strategy', icon: 'lock' }
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const assetData = await getAsset(assetId);
        setAsset(assetData.asset);
        setEnrichment(assetData.enrichment);

        if (assetData.asset?.lastResultId) {
          const details = await getAssetScanDetails(assetId, assetData.asset.lastResultId);
          setScanDetails(details);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (assetId) {
      fetchData();
    }
  }, [assetId]);

  // Track scroll position to update active section
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 150;

      for (const section of sections) {
        const ref = sectionRefs[section.id];
        if (ref.current) {
          const { offsetTop, offsetHeight } = ref.current;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollToSection = (sectionId) => {
    const ref = sectionRefs[sectionId];
    if (ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const toggleSection = (sectionId) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const getSectionIcon = (iconName) => {
    const icons = {
      shield: '\u25C8',
      target: '\u25CE',
      warning: '\u26A0',
      flask: '\u2697',
      lock: '\u2610'
    };
    return icons[iconName] || '\u25CF';
  };

  // Calculate vulnerability stats
  const getVulnStats = () => {
    if (!scanDetails?.fullResult) return { critical: 0, high: 0, medium: 0, low: 0, info: 0, total: 0 };

    const findings = scanDetails.fullResult.findings ||
      scanDetails.fullResult.stageResults?.vulnScanning?.findings || [];

    const stats = { critical: 0, high: 0, medium: 0, low: 0, info: 0 };
    findings.forEach(f => {
      const sev = (f.severity || f.info?.severity || 'info').toLowerCase();
      if (stats[sev] !== undefined) stats[sev]++;
    });
    stats.total = findings.length;
    return stats;
  };

  const vulnStats = getVulnStats();

  if (loading) {
    return (
      <div className="dossier-scroll-page">
        <div className="scroll-loading">
          <div className="scroll-loading-spinner"></div>
          <p>Loading asset intelligence...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dossier-scroll-page">
        <div className="scroll-error">
          <span className="error-icon">\u26A0</span>
          <p>{error}</p>
          <button onClick={() => navigate('/assets')}>Return to Assets</button>
        </div>
      </div>
    );
  }

  const findings = scanDetails?.fullResult?.findings ||
    scanDetails?.fullResult?.stageResults?.vulnScanning?.findings || [];
  const subdomains = enrichment?.subdomains || [];
  const ports = scanDetails?.fullResult?.stageResults?.portScanning?.ports || [];

  return (
    <div className="dossier-scroll-page">
      {/* Sticky Side Navigation */}
      <nav className="scroll-side-nav">
        <div className="side-nav-header">
          <button className="back-btn" onClick={() => navigate('/assets')}>
            \u2190 Assets
          </button>
        </div>

        <div className="side-nav-target">
          <span className="target-label">TARGET</span>
          <span className="target-value">{asset?.target || 'Unknown'}</span>
        </div>

        <ul className="side-nav-links">
          {sections.map(section => (
            <li key={section.id}>
              <button
                className={`nav-link ${activeSection === section.id ? 'active' : ''}`}
                onClick={() => scrollToSection(section.id)}
              >
                <span className="nav-icon">{getSectionIcon(section.icon)}</span>
                <span className="nav-label">{section.label}</span>
              </button>
            </li>
          ))}
        </ul>

        <div className="side-nav-stats">
          <div className="nav-stat critical">
            <span className="stat-value">{vulnStats.critical}</span>
            <span className="stat-label">Critical</span>
          </div>
          <div className="nav-stat high">
            <span className="stat-value">{vulnStats.high}</span>
            <span className="stat-label">High</span>
          </div>
          <div className="nav-stat medium">
            <span className="stat-value">{vulnStats.medium}</span>
            <span className="stat-label">Medium</span>
          </div>
        </div>
      </nav>

      {/* Main Scrolling Content */}
      <main className="scroll-main-content">
        {/* Hero Header */}
        <header className="scroll-hero">
          <div className="hero-content">
            <h1 className="hero-target">{asset?.target}</h1>
            <div className="hero-meta">
              <span className="meta-item">
                <span className="meta-icon">\u2606</span>
                {asset?.assetType || 'domain'}
              </span>
              <span className="meta-item">
                <span className="meta-icon">\u2609</span>
                Last scan: {asset?.lastScannedAt ? new Date(asset.lastScannedAt).toLocaleDateString() : 'Never'}
              </span>
              <span className="meta-item">
                <span className="meta-icon">\u260E</span>
                {vulnStats.total} findings
              </span>
            </div>
          </div>
          <div className="hero-risk">
            <span className={`risk-badge ${vulnStats.critical > 0 ? 'critical' : vulnStats.high > 0 ? 'high' : 'healthy'}`}>
              {vulnStats.critical > 0 ? 'CRITICAL RISK' : vulnStats.high > 0 ? 'HIGH RISK' : 'HEALTHY'}
            </span>
          </div>
        </header>

        {/* Summary Section */}
        <section ref={sectionRefs.summary} id="summary" className="scroll-section">
          <div className="section-header" onClick={() => toggleSection('summary')}>
            <h2>
              <span className="section-icon">{getSectionIcon('shield')}</span>
              Security Summary
            </h2>
            <span className={`collapse-icon ${collapsedSections.summary ? 'collapsed' : ''}`}>\u25BC</span>
          </div>

          {!collapsedSections.summary && (
            <div className="section-content">
              <div className="summary-grid">
                <div className="summary-card">
                  <span className="card-icon critical">\u26A0</span>
                  <div className="card-info">
                    <span className="card-value">{vulnStats.critical}</span>
                    <span className="card-label">Critical</span>
                  </div>
                </div>
                <div className="summary-card">
                  <span className="card-icon high">\u26A0</span>
                  <div className="card-info">
                    <span className="card-value">{vulnStats.high}</span>
                    <span className="card-label">High</span>
                  </div>
                </div>
                <div className="summary-card">
                  <span className="card-icon medium">\u26A0</span>
                  <div className="card-info">
                    <span className="card-value">{vulnStats.medium}</span>
                    <span className="card-label">Medium</span>
                  </div>
                </div>
                <div className="summary-card">
                  <span className="card-icon low">\u26A0</span>
                  <div className="card-info">
                    <span className="card-value">{vulnStats.low}</span>
                    <span className="card-label">Low</span>
                  </div>
                </div>
              </div>

              <div className="key-insights">
                <h3>Key Insights</h3>
                <ul className="insights-list">
                  {vulnStats.critical > 0 && (
                    <li className="insight critical">
                      <span className="insight-icon">\u2717</span>
                      {vulnStats.critical} critical vulnerabilities require immediate attention
                    </li>
                  )}
                  <li className="insight">
                    <span className="insight-icon">\u2713</span>
                    {subdomains.length} subdomains discovered
                  </li>
                  <li className="insight">
                    <span className="insight-icon">\u2713</span>
                    {ports.filter(p => p.state === 'open').length} open ports identified
                  </li>
                </ul>
              </div>
            </div>
          )}
        </section>

        {/* Attack Surface Section */}
        <section ref={sectionRefs.attackSurface} id="attackSurface" className="scroll-section">
          <div className="section-header" onClick={() => toggleSection('attackSurface')}>
            <h2>
              <span className="section-icon">{getSectionIcon('target')}</span>
              Attack Surface
            </h2>
            <span className={`collapse-icon ${collapsedSections.attackSurface ? 'collapsed' : ''}`}>\u25BC</span>
          </div>

          {!collapsedSections.attackSurface && (
            <div className="section-content">
              {/* Subdomains */}
              <div className="surface-subsection">
                <h3>Discovered Subdomains ({subdomains.length})</h3>
                {subdomains.length > 0 ? (
                  <div className="subdomain-list">
                    {subdomains.slice(0, 20).map((sub, idx) => (
                      <div key={idx} className="subdomain-item">
                        <span className="subdomain-name">{sub.host || sub}</span>
                        {sub.ip && <span className="subdomain-ip">{sub.ip}</span>}
                      </div>
                    ))}
                    {subdomains.length > 20 && (
                      <div className="more-indicator">+{subdomains.length - 20} more subdomains</div>
                    )}
                  </div>
                ) : (
                  <p className="no-data">No subdomains discovered</p>
                )}
              </div>

              {/* Open Ports */}
              <div className="surface-subsection">
                <h3>Open Ports ({ports.filter(p => p.state === 'open').length})</h3>
                {ports.length > 0 ? (
                  <div className="port-grid">
                    {ports.filter(p => p.state === 'open').map((port, idx) => (
                      <div key={idx} className="port-card">
                        <span className="port-number">{port.port}</span>
                        <span className="port-service">{port.service || 'unknown'}</span>
                        <span className="port-protocol">{port.protocol || 'tcp'}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="no-data">No port scan data available</p>
                )}
              </div>

              {/* Technologies */}
              {enrichment?.technologies && enrichment.technologies.length > 0 && (
                <div className="surface-subsection">
                  <h3>Detected Technologies</h3>
                  <div className="tech-tags">
                    {enrichment.technologies.map((tech, idx) => (
                      <span key={idx} className="tech-tag">{tech.name || tech}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </section>

        {/* Vulnerabilities Section */}
        <section ref={sectionRefs.vulnerabilities} id="vulnerabilities" className="scroll-section">
          <div className="section-header" onClick={() => toggleSection('vulnerabilities')}>
            <h2>
              <span className="section-icon">{getSectionIcon('warning')}</span>
              Vulnerabilities ({findings.length})
            </h2>
            <span className={`collapse-icon ${collapsedSections.vulnerabilities ? 'collapsed' : ''}`}>\u25BC</span>
          </div>

          {!collapsedSections.vulnerabilities && (
            <div className="section-content">
              {findings.length > 0 ? (
                <div className="vuln-timeline">
                  {findings.map((finding, idx) => {
                    const severity = (finding.severity || finding.info?.severity || 'info').toLowerCase();
                    return (
                      <div key={idx} className={`vuln-entry ${severity}`}>
                        <div className="vuln-marker">
                          <span className={`severity-dot ${severity}`}></span>
                        </div>
                        <div className="vuln-content">
                          <div className="vuln-header">
                            <h4>{finding.info?.name || finding.templateId || 'Unknown'}</h4>
                            <span className={`severity-badge ${severity}`}>{severity.toUpperCase()}</span>
                          </div>
                          <p className="vuln-description">
                            {finding.info?.description?.substring(0, 200) || 'No description available'}
                            {finding.info?.description?.length > 200 && '...'}
                          </p>
                          {finding.matchedAt && (
                            <span className="vuln-location">Found at: {finding.matchedAt}</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="no-vulns">
                  <span className="no-vulns-icon">\u2713</span>
                  <p>No vulnerabilities detected</p>
                </div>
              )}
            </div>
          )}
        </section>

        {/* Testing Approach Section */}
        <section ref={sectionRefs.testing} id="testing" className="scroll-section">
          <div className="section-header" onClick={() => toggleSection('testing')}>
            <h2>
              <span className="section-icon">{getSectionIcon('flask')}</span>
              Testing Approach
            </h2>
            <span className={`collapse-icon ${collapsedSections.testing ? 'collapsed' : ''}`}>\u25BC</span>
          </div>

          {!collapsedSections.testing && (
            <div className="section-content">
              <div className="approach-flow">
                <div className="approach-step">
                  <div className="step-number">1</div>
                  <div className="step-content">
                    <h4>Reconnaissance</h4>
                    <p>Enumerate subdomains, identify infrastructure, and map the attack surface.</p>
                    <ul className="step-tasks">
                      <li>Subdomain enumeration</li>
                      <li>DNS analysis</li>
                      <li>Technology fingerprinting</li>
                    </ul>
                  </div>
                </div>

                <div className="approach-step">
                  <div className="step-number">2</div>
                  <div className="step-content">
                    <h4>Service Discovery</h4>
                    <p>Identify running services, open ports, and exposed endpoints.</p>
                    <ul className="step-tasks">
                      <li>Port scanning</li>
                      <li>Service identification</li>
                      <li>Version detection</li>
                    </ul>
                  </div>
                </div>

                <div className="approach-step">
                  <div className="step-number">3</div>
                  <div className="step-content">
                    <h4>Vulnerability Assessment</h4>
                    <p>Test for known vulnerabilities and security misconfigurations.</p>
                    <ul className="step-tasks">
                      <li>CVE scanning</li>
                      <li>Configuration auditing</li>
                      <li>Security header analysis</li>
                    </ul>
                  </div>
                </div>

                <div className="approach-step">
                  <div className="step-number">4</div>
                  <div className="step-content">
                    <h4>Validation</h4>
                    <p>Confirm findings and assess exploitability without causing harm.</p>
                    <ul className="step-tasks">
                      <li>Safe validation tests</li>
                      <li>Impact assessment</li>
                      <li>Proof of concept</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}
        </section>

        {/* Defense Strategy Section */}
        <section ref={sectionRefs.defense} id="defense" className="scroll-section">
          <div className="section-header" onClick={() => toggleSection('defense')}>
            <h2>
              <span className="section-icon">{getSectionIcon('lock')}</span>
              Defense Strategy
            </h2>
            <span className={`collapse-icon ${collapsedSections.defense ? 'collapsed' : ''}`}>\u25BC</span>
          </div>

          {!collapsedSections.defense && (
            <div className="section-content">
              <div className="defense-priorities">
                <h3>Prioritized Actions</h3>

                {vulnStats.critical > 0 && (
                  <div className="priority-block critical">
                    <div className="priority-header">
                      <span className="priority-badge">IMMEDIATE</span>
                      <span className="priority-label">Critical Vulnerabilities</span>
                    </div>
                    <p>Address {vulnStats.critical} critical vulnerabilities immediately. These represent the highest risk to your organization.</p>
                  </div>
                )}

                {vulnStats.high > 0 && (
                  <div className="priority-block high">
                    <div className="priority-header">
                      <span className="priority-badge">SHORT-TERM</span>
                      <span className="priority-label">High Severity Issues</span>
                    </div>
                    <p>Remediate {vulnStats.high} high severity findings within the next sprint cycle.</p>
                  </div>
                )}

                <div className="priority-block medium">
                  <div className="priority-header">
                    <span className="priority-badge">ONGOING</span>
                    <span className="priority-label">Continuous Monitoring</span>
                  </div>
                  <p>Schedule regular scans and monitor for new vulnerabilities as they emerge.</p>
                </div>
              </div>

              <div className="defense-recommendations">
                <h3>General Recommendations</h3>
                <ul className="recommendations-list">
                  <li>
                    <span className="rec-icon">\u2022</span>
                    Implement a web application firewall (WAF) to protect against common attacks
                  </li>
                  <li>
                    <span className="rec-icon">\u2022</span>
                    Enable security headers (CSP, HSTS, X-Frame-Options)
                  </li>
                  <li>
                    <span className="rec-icon">\u2022</span>
                    Regular security assessments and penetration testing
                  </li>
                  <li>
                    <span className="rec-icon">\u2022</span>
                    Keep all software and dependencies up to date
                  </li>
                  <li>
                    <span className="rec-icon">\u2022</span>
                    Implement proper access controls and authentication
                  </li>
                </ul>
              </div>
            </div>
          )}
        </section>

        {/* Footer */}
        <footer className="scroll-footer">
          <p>Asset Dossier - Scroll View | DriftAlarm</p>
          <p className="footer-proto">UX Prototype - Variation B</p>
        </footer>
      </main>
    </div>
  );
};

export default AssetDossierScroll;
